import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PREF_ROWS = [
  {
    key: "payment_received",
    title: "Money-in alerts",
    desc: "Email me when a client's crypto payment confirms on-chain.",
    testid: "pref-payment-received",
  },
  {
    key: "client_receipts",
    title: "Client receipts",
    desc: "Send your clients an automatic payment-confirmed receipt.",
    testid: "pref-client-receipts",
  },
  {
    key: "split_scheduled",
    title: "Collaborator split alerts",
    desc: "Notify collaborators when their revenue split is scheduled.",
    testid: "pref-split-scheduled",
  },
];

export const NotificationsCard = () => {
  const { user, refresh } = useAuth();
  const [prefs, setPrefs] = useState({
    payment_received: user?.notification_prefs?.payment_received ?? true,
    client_receipts: user?.notification_prefs?.client_receipts ?? true,
    split_scheduled: user?.notification_prefs?.split_scheduled ?? true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.notification_prefs) {
      setPrefs({
        payment_received: user.notification_prefs.payment_received ?? true,
        client_receipts: user.notification_prefs.client_receipts ?? true,
        split_scheduled: user.notification_prefs.split_scheduled ?? true,
      });
    }
  }, [user?.notification_prefs]);

  const togglePref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setBusy(true);
    try {
      await api.put("/profile", { notification_prefs: next });
      await refresh();
      toast.success("Notification preferences updated");
    } catch {
      setPrefs(prefs);
      toast.error("Couldn't update preferences");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="md:col-span-7 border border-[#262626] p-6" data-testid="notifications-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <Bell size={12} /> Notifications
        </div>
        {busy && <Loader2 size={14} className="animate-spin text-zinc-500" />}
      </div>
      <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
        Quill watches your wallet and fires transactional emails the moment a payment confirms on-chain. Toggle anything you don't want.
      </p>
      <div className="divide-y divide-[#262626]">
        {PREF_ROWS.map((row) => (
          <div key={row.key} className="flex items-start justify-between gap-6 py-4">
            <div>
              <div className="text-sm text-white">{row.title}</div>
              <div className="text-xs text-zinc-500 mt-1 max-w-md">{row.desc}</div>
            </div>
            <Switch
              data-testid={row.testid}
              checked={prefs[row.key]}
              disabled={busy}
              onCheckedChange={(v) => togglePref(row.key, v)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};
